import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

import PrimaryButton from '../../shared/button/PrimaryButton';
import { CaptionText, MinorHeading } from '../../shared/typography/PrimaryTypography';
import {
  fetchMetricsByIds,
  toProblemDetail,
  updateRecord
} from '../../../services/syntiro_honey/data_ingestion.service';
import {
  ComplianceValue,
  EntityCandidate,
  EntityLookupItem,
  EsgRecord,
  MetricLookupItem,
  ProblemDetail,
  UpdateRecordPatch
} from '../../../types/syntiro_honey';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { aliasOutcomeFeedback } from './alias_feedback';
import ConfirmMappingButton from './ConfirmMappingButton';
import EntityCandidatesPicker from './EntityCandidatesPicker';
import EntitySelect from './EntitySelect';
import LocationSelect from './LocationSelect';
import MappingCandidatesPicker from './MappingCandidatesPicker';
import MetricAutocomplete from './MetricAutocomplete';
import PeriodSelect from './PeriodSelect';
import { resolveRecordEntity } from './record_entity';
import { formatDisplayValue } from './value_format';

/** Normalised values must be decimal strings (e.g. "123.45", "-7"). */
const DECIMAL_STRING_PATTERN = /^-?\d+(\.\d+)?$/;

const COMPLIANCE_OPTIONS: Array<ComplianceValue> = ['YES', 'NO', 'PARTIAL'];

interface RecordEditFormProps {
  /** The record being edited (remount the form via `key` when it changes). */
  record: EsgRecord;
  /** Called with the updated record after a successful save. */
  onSaved: (updated: EsgRecord) => void;
}

/** Index catalogue metrics by id, merged over what is already known. */
function withMetrics(
  current: Record<string, MetricLookupItem>,
  items: Array<MetricLookupItem>
): Record<string, MetricLookupItem> {
  const merged = { ...current };
  items.forEach((item) => {
    merged[item.id] = item;
  });
  return merged;
}

/**
 * Describe where the record's location comes from. A chosen site owns the
 * location; a site-less record may carry a country/province of its own (OQ27b),
 * edited via the pickers below. Naming the source stops a reviewer wondering
 * which of the two applies.
 */
function describeLocation(
  record: EsgRecord,
  draft: {
    siteId: string;
    countryId: string;
    provinceId: string;
  }
): string {
  if (draft.siteId !== '') {
    // The saved names describe the SAVED site, so once a different site is picked
    // they are stale; promise the update rather than showing the old location.
    if (draft.siteId !== (record.siteId ?? '')) {
      return 'Location: follows the chosen site — updates on save';
    }
    const known = [record.countryName, record.provinceName].filter(
      (part): part is string => part !== null && part !== ''
    );
    if (known.length === 0) {
      return 'Location: none recorded for this site';
    }
    return `Location: ${known.join(' · ')} (from the site)`;
  }
  if (draft.countryId === '') {
    return 'Location: none — pick a site, or set a country/province below';
  }
  const unchanged =
    draft.countryId === (record.countryId ?? '') && draft.provinceId === (record.provinceId ?? '');
  if (!unchanged) {
    return 'Location: set directly on the record — updates on save';
  }
  const known = [record.countryName, record.provinceName].filter(
    (part): part is string => part !== null && part !== ''
  );
  return `Location: ${known.join(' · ')} (set directly on the record)`;
}

/**
 * Trim a nullable-FK draft to its PATCH value: unchanged → omit; emptied → an
 * explicit null (the API clears the field); anything else → the new id.
 */
function toNullablePatchField(draft: string, current: string | null): string | null | undefined {
  const trimmed = draft.trim();
  if (trimmed === (current ?? '')) {
    return undefined;
  }
  return trimmed === '' ? null : trimmed;
}

/** Trim a draft and treat empty as "no change" (the PATCH cannot clear fields). */
function toPatchField(draft: string, current: string | null): string | undefined {
  const trimmed = draft.trim();
  if (trimmed === '' || trimmed === (current ?? '')) {
    return undefined;
  }
  return trimmed;
}

/**
 * Edit form for a staged ESG record: normalised value, canonical unit, metric
 * id (free text plus the mapping-candidates picker), site id, the qualitative
 * verdict + note, and the record note. Only changed fields are PATCHed; each
 * is audited as an EDITED event server-side.
 * @returns {JSX.Element}
 */
export default function RecordEditForm({ record, onSaved }: RecordEditFormProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [value, setValue] = useState<string>(record.value ?? '');
  const [canonicalUnit, setCanonicalUnit] = useState<string>(record.canonicalUnit ?? '');
  const [metricId, setMetricId] = useState<string>(record.metricId ?? '');
  const [divisionId, setDivisionId] = useState<string>(record.divisionId ?? '');
  const [subdivisionId, setSubdivisionId] = useState<string>(record.subdivisionId ?? '');
  const [siteId, setSiteId] = useState<string>(record.siteId ?? '');
  const [companyWide, setCompanyWide] = useState<boolean>(record.companyWide === true);
  const [countryId, setCountryId] = useState<string>(record.countryId ?? '');
  const [provinceId, setProvinceId] = useState<string>(record.provinceId ?? '');
  const [periodId, setPeriodId] = useState<string>(record.periodId ?? '');
  const [complianceValue, setComplianceValue] = useState<ComplianceValue | null>(
    record.complianceValue
  );
  const [qualitativeNote, setQualitativeNote] = useState<string>(record.qualitativeNote ?? '');
  const [note, setNote] = useState<string>(record.note ?? '');
  const [saving, setSaving] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  // Catalogue detail for every metric the reviewer can reach from this form: the
  // candidates (fetched by id below) plus whatever the autocomplete hands back. It
  // answers both "what unit does this expect?" and "is this metric qualitative?"
  // for the metric currently in the DRAFT, which the record itself cannot.
  const [metricsById, setMetricsById] = useState<Record<string, MetricLookupItem>>({});
  // The unit this form last auto-filled, so a follow-up metric pick may replace
  // its own suggestion but never a unit the reviewer typed deliberately.
  const lastAutoFilledUnit = useRef<string | null>(null);

  /**
   * Auto-fill the canonical unit from a picked metric (Maxine R4 ③): the expected
   * unit lands in the field immediately — visible and editable before save — so a
   * manual remap no longer needs a separate unit correction. Only a unit the
   * reviewer has NOT made their own is replaced: blank, the record's saved unit,
   * or this form's previous auto-fill.
   */
  const applyMetricUnit = (metric: MetricLookupItem | undefined): void => {
    const expectedUnit = metric?.unit ?? null;
    if (expectedUnit === null) {
      return; // Qualitative or unknown metric — nothing to suggest.
    }
    const current = canonicalUnit.trim();
    const replaceable =
      current === '' ||
      current === (record.canonicalUnit ?? '') ||
      current === lastAutoFilledUnit.current;
    if (replaceable) {
      setCanonicalUnit(expectedUnit);
      lastAutoFilledUnit.current = expectedUnit;
    }
  };

  // Joined into a string so the effect below depends on a stable primitive: the
  // record's candidate array is a fresh object on every render.
  const candidateMetricIds = (record.mappingCandidates ?? [])
    .map((candidate) => candidate.metricId)
    .join(',');

  useEffect(() => {
    const ids = candidateMetricIds === '' ? [] : candidateMetricIds.split(',');
    let cancelled = false;
    fetchMetricsByIds(ids)
      .then((items) => {
        // An empty result carries no new information, and setting state anyway
        // would re-render every form whose record has no candidates at all.
        if (!cancelled && items.length > 0) {
          setMetricsById((current) => withMetrics(current, items));
        }
      })
      // A failed lookup only costs the "expects <unit>" line; the picker still works.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [candidateMetricIds]);

  const valueDraft = value.trim();
  const valueInvalid =
    valueDraft !== '' &&
    valueDraft !== (record.value ?? '') &&
    !DECIMAL_STRING_PATTERN.test(valueDraft);

  // While a site is chosen the location belongs to it: the geography drafts are
  // not sent (the API clears any stored direct geography itself when the site
  // lands, and rejects a direct set beside a site).
  const siteChosen = siteId.trim() !== '';
  const patch: UpdateRecordPatch = {
    value: valueInvalid ? undefined : toPatchField(value, record.value),
    canonicalUnit: toPatchField(canonicalUnit, record.canonicalUnit),
    metricId: toPatchField(metricId, record.metricId),
    // Entity FKs are nullable on the wire: emptying a draft sends an explicit
    // null CLEAR, so "move this record up the hierarchy" actually takes — a
    // stored site would otherwise survive the edit and keep the site-lock engaged.
    divisionId: toNullablePatchField(divisionId, record.divisionId),
    subdivisionId: toNullablePatchField(subdivisionId, record.subdivisionId),
    siteId: toNullablePatchField(siteId, record.siteId),
    // Sent only when the placement actually changes. Choosing the company clears
    // every entity draft to a null CLEAR, which the API accepts beside the marker
    // (a clear is not an attachment); an entity ID beside it stays rejected.
    companyWide: companyWide !== (record.companyWide === true) ? companyWide : undefined,
    countryId: siteChosen ? undefined : toNullablePatchField(countryId, record.countryId ?? null),
    provinceId: siteChosen
      ? undefined
      : toNullablePatchField(provinceId, record.provinceId ?? null),
    periodId: toPatchField(periodId, record.periodId),
    complianceValue:
      complianceValue !== null && complianceValue !== record.complianceValue
        ? complianceValue
        : undefined,
    qualitativeNote: toPatchField(qualitativeNote, record.qualitativeNote),
    note: toPatchField(note, record.note)
  };
  const hasChanges = Object.values(patch).some((field) => field !== undefined);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaved(false);
    try {
      const updated = await updateRecord(record.id, patch);
      setProblem(null);
      setSaved(true);
      if (patch.metricId !== undefined) {
        // A metric change is also a confirmation that teaches the per-client
        // learned rule — surface what it taught, exactly like the confirm button.
        setFeedbackData(aliasOutcomeFeedback(updated));
      }
      onSaved(updated);
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setSaving(false);
    }
  };

  const entityCandidates = record.entityCandidates ?? [];
  // The deepest entity id currently chosen (site → subdivision → division), for
  // highlighting the picked candidate.
  const chosenEntityId =
    [siteId, subdivisionId, divisionId].map((id) => id.trim()).find((id) => id !== '') ?? null;

  // The record's currently-resolved entity and the LEVEL it attaches at (site /
  // sub-division / division). Surfaced so a record resolved above site level reads
  // as intentionally attributed, not as one with a missing site.
  const resolvedEntity = resolveRecordEntity(record);

  const locationCaption = describeLocation(record, {
    siteId: siteId.trim(),
    countryId: countryId.trim(),
    provinceId: provinceId.trim()
  });

  /** Apply a picked entity candidate to the right FK level(s). */
  const handleSelectEntity = (candidate: EntityCandidate): void => {
    setSiteId(candidate.siteId ?? '');
    setSubdivisionId(candidate.subdivisionId ?? '');
    setDivisionId(candidate.divisionId ?? '');
    // A candidate always names an entity WITHIN the company, so it lifts the
    // company-wide marker — without this, a candidate click after a COMPANY pick
    // built a companyWide+FK patch the API rejects as contradictory.
    setCompanyWide(false);
    // A chosen site owns the location — the direct geography drafts step aside.
    if (candidate.siteId) {
      setCountryId('');
      setProvinceId('');
    }
  };

  /** Apply an entity chosen from the catalogue dropdown to the right FK level(s). */
  const handleSelectLookupEntity = (entity: EntityLookupItem): void => {
    setSiteId(entity.siteId ?? '');
    setSubdivisionId(entity.subdivisionId ?? '');
    setDivisionId(entity.divisionId ?? '');
    // Picking the COMPANY row sets no FK at all, so the marker is what records the
    // choice; picking anything deeper lifts it (Maxine ⑨).
    setCompanyWide(entity.kind === 'COMPANY');
    if (entity.siteId !== null) {
      setCountryId('');
      setProvinceId('');
    }
  };

  const candidates = record.mappingCandidates ?? [];
  // Resolve a display name for the selected metric without a server round-trip:
  // the record's own join when unchanged, else a matching candidate's name.
  const metricDisplayName =
    metricId === record.metricId
      ? record.metricName
      : (candidates.find((candidate) => candidate.metricId === metricId)?.name ?? null);
  // The Yes/No/Partial verdict only makes sense on a qualitative metric; the
  // controls also stay visible while a verdict exists (so it remains editable
  // even if the mapping later changes).
  //
  // Read from the DRAFT metric, not the saved record. Keying off the record meant
  // picking a qualitative metric left the verdict controls hidden until after Save
  // — so the reviewer saved an incomplete record, reopened it, and edited again.
  const draftMetric = metricsById[metricId.trim()];
  const showQualitativeControls =
    (draftMetric !== undefined ? draftMetric.isQualitative : record.metricIsQualitative === true) ||
    record.complianceValue !== null;

  // The unit UI must follow the metric the reviewer is PICKING, not the one the
  // record was saved with (Maxine R4 ③): during a draft remap the picked metric's
  // expected unit is the one sensible quick-pick, so it replaces the stale
  // suggestions computed for the old (or missing) metric.
  const draftRemap = draftMetric !== undefined && metricId.trim() !== (record.metricId ?? '');
  const unitSuggestions =
    draftRemap && draftMetric.unit !== null ? [draftMetric.unit] : record.unitSuggestions;
  const unitNudge =
    draftRemap && draftMetric.unit !== null
      ? `Expected unit: ${draftMetric.unit}`
      : record.unitNudge;

  return (
    <Stack spacing={2}>
      <MinorHeading weight="Medium">Edit record</MinorHeading>
      {problem !== null && (
        <Alert severity="error">{problem.detail ?? problem.title ?? 'Save failed.'}</Alert>
      )}
      {saved && <Alert severity="success">Record saved.</Alert>}
      <Stack direction="row" spacing={2}>
        <TextField
          label="Value (normalised)"
          size="small"
          fullWidth
          value={value}
          onChange={(event) => setValue(event.target.value)}
          error={valueInvalid}
          helperText={valueInvalid ? 'Must be a decimal number, e.g. 123.45' : undefined}
        />
        <TextField
          label="Canonical unit"
          size="small"
          fullWidth
          value={canonicalUnit}
          onChange={(event) => setCanonicalUnit(event.target.value)}
        />
      </Stack>
      {/* The immutable client original, incl. the unit derived from the metric label
          ("Charcoal (kg)" → 20 kg). Shown read-only so a reviewer editing the
          normalised value can see the source unit, not just the canonical (Maxine ③). */}
      {record.rawValue !== null && (
        <CaptionText>
          Client value (original): {formatDisplayValue(record.rawValue)} {record.rawUnit ?? ''}
        </CaptionText>
      )}
      {unitSuggestions.length > 0 && (
        <Stack spacing={0.5}>
          <CaptionText weight="Medium">
            Unit suggestions{unitNudge !== null ? ` — ${unitNudge}` : ''}
          </CaptionText>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {unitSuggestions.slice(0, 8).map((unit) => (
              <Chip
                key={unit}
                label={unit}
                size="small"
                variant={canonicalUnit === unit ? 'filled' : 'outlined'}
                color={canonicalUnit === unit ? 'primary' : 'default'}
                onClick={() => setCanonicalUnit(unit)}
              />
            ))}
          </Stack>
        </Stack>
      )}
      <MetricAutocomplete
        label="Metric"
        selectedId={metricId.trim() === '' ? null : metricId.trim()}
        selectedLabel={metricDisplayName}
        onSelect={(metric) => {
          setMetricId(metric?.id ?? '');
          // Remember what the catalogue says about it, so a metric chosen HERE
          // drives the qualitative controls exactly like a picked candidate does.
          if (metric !== null) {
            setMetricsById((current) => withMetrics(current, [metric]));
          }
          applyMetricUnit(metric ?? undefined);
        }}
      />
      {/* Teaching is intentional, not a surprise: a metric change is a durable
          per-client rule (the learning loop), so say so BEFORE the save. */}
      {patch.metricId !== undefined && (
        <CaptionText>
          Saving teaches a rule for this client: &ldquo;{record.rawLabel}&rdquo; will map to{' '}
          {metricDisplayName ?? 'the chosen metric'} automatically on future uploads.
        </CaptionText>
      )}
      {candidates.length > 0 && (
        <MappingCandidatesPicker
          candidates={candidates}
          selectedMetricId={metricId.trim() === '' ? null : metricId.trim()}
          onSelect={(id) => {
            setMetricId(id);
            applyMetricUnit(metricsById[id]);
          }}
          rawLabel={record.rawLabel}
          unmapped={record.metricId === null}
          metricsById={metricsById}
        />
      )}
      {entityCandidates.length > 0 && (
        <Stack spacing={0.5}>
          <CaptionText weight="Medium">Entity candidates</CaptionText>
          <EntityCandidatesPicker
            candidates={entityCandidates}
            selectedEntityId={chosenEntityId}
            onSelect={handleSelectEntity}
          />
        </Stack>
      )}
      <CaptionText weight="Medium">
        Resolved entity:{' '}
        {resolvedEntity !== null
          ? `${resolvedEntity.name} · ${resolvedEntity.level}`
          : 'Unattributed'}
      </CaptionText>
      {/* Pick the entity by NAME from the catalogue (Maxine ①) — no raw id shown; the
          dropdown scopes to the record's own company and sets the right FK level. */}
      <EntitySelect
        label="Entity"
        divisionId={divisionId.trim() === '' ? null : divisionId.trim()}
        subdivisionId={subdivisionId.trim() === '' ? null : subdivisionId.trim()}
        siteId={siteId.trim() === '' ? null : siteId.trim()}
        currentLabel={resolvedEntity?.name ?? null}
        onSelect={handleSelectLookupEntity}
        companyId={record.companyId ?? null}
        companyWide={companyWide}
      />
      {/* A chosen site owns the location; a SITE-LESS record may attach to a country
          or province directly (Maxine, OQ27b) — clients report whole-country and
          whole-province slices with no site. The pickers disable when a site is
          chosen, so the two sources can never contradict. */}
      <CaptionText weight="Medium">{locationCaption}</CaptionText>
      <LocationSelect
        countryId={countryId.trim() === '' ? null : countryId.trim()}
        provinceId={provinceId.trim() === '' ? null : provinceId.trim()}
        companyId={record.companyId ?? null}
        disabled={siteChosen}
        onChange={(nextCountryId, nextProvinceId) => {
          setCountryId(nextCountryId ?? '');
          setProvinceId(nextProvinceId ?? '');
        }}
      />
      {/* Reporting period is a dimension correction (Maxine #9): fix a misread month/FY
          without touching value/mapping confidence. Scoped to the record's company so
          the correction can only pick from THAT client's calendar (OQ25). */}
      <PeriodSelect
        label="Reporting period"
        value={periodId === '' ? null : periodId}
        onChange={(next) => setPeriodId(next ?? '')}
        companyId={record.companyId ?? null}
        currentLabel={record.periodLabel}
      />
      {showQualitativeControls && (
        <>
          <Stack spacing={0.5}>
            <CaptionText weight="Medium">Qualitative verdict</CaptionText>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={complianceValue}
              onChange={(_event, next: ComplianceValue | null) => {
                // Ignore deselection: the PATCH cannot clear the verdict.
                if (next !== null) {
                  setComplianceValue(next);
                }
              }}
            >
              {COMPLIANCE_OPTIONS.map((option) => (
                <ToggleButton key={option} value={option}>
                  {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <TextField
            label="Qualitative note"
            size="small"
            fullWidth
            multiline
            value={qualitativeNote}
            onChange={(event) => setQualitativeNote(event.target.value)}
          />
        </>
      )}
      <TextField
        label="Record note"
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-end' }}>
        {/* Agreeing with a suggested mapping is not a "change", so `toPatchField`
            reports nothing to save and the button below stays disabled. Without
            this, the only way to confirm from the modal was to approve the whole
            record — even when the value still needed checking. The button hides
            itself unless the mapping is SUGGESTED and the record is still open. */}
        <ConfirmMappingButton record={record} onConfirmed={onSaved} />
        <PrimaryButton
          onClick={() => void handleSave()}
          disabled={!hasChanges || valueInvalid || saving}
          loading={saving}
        >
          Save changes
        </PrimaryButton>
      </Stack>
    </Stack>
  );
}
